/**
 * Profiles where the "suggest another doctor" surfaces (profile card under the no-booking notice,
 * booking-page «نوبت پر شده» modal) list other same-city specialists that can be booked now instead
 * of an online visit. Per-profile on purpose (owner requests 2026-10-19: Dr. Taheri, Dr. Asilian,
 * Dr. Norouzi; 2026-10-23: Dr. Ebrahimi, Dr. Sakhanvar, Dr. Talebian) —
 * every other profile keeps the online-visit suggestion.
 */
export interface LocalPrivateSpecialistsConfig {
  /** Search city slug (route segment), e.g. `shahrekord`. */
  city: string;
  cityName: string;
  /** Search category slug (route segment), e.g. `imaging`. */
  groupSlug: string;
  /** Specialty shown in the heading, e.g. «رادیولوژی». */
  expertiseName: string;
  /** Hits whose `display_expertise` mentions none of these are dropped (other specialties in the group). */
  includeExpertise: string[];
  /** Hits whose `display_expertise` matches any of these are dropped (e.g. dental radiology, residents). */
  excludeExpertise?: string[];
  /** Search only private offices («پزشکان مطبی»). Default true; false = clinics and hospitals too. */
  privateOfficesOnly?: boolean;
  /**
   * Big cities: the city route also returns the province's towns. A doctor qualifies when one of
   * their centers has coordinates within `radiusKm` of `lat`/`lon`, or — for centers without usable
   * coordinates (missing, the 1/1 placeholder, or absurdly far) — when its `city_name` is `cityName`.
   * Only used to keep the list in the city; distance is never shown.
   */
  near?: { lat: number; lon: number; radiusKm: number };
  /** Quality floor: search satisfaction (%) and number of reviews (`rates_count`). */
  minSatisfaction?: number;
  minRates?: number;
  /** `first-turn`: earliest free turn first, then satisfaction. Default `satisfaction` (smoothed). */
  order?: 'satisfaction' | 'first-turn';
  /** Also show the suggestion while this doctor's own booking is open (under the booking card). */
  showWhenBookable?: boolean;
  /** Search result pages to scan (the API serves at most 50 per page). Default 1. */
  searchPages?: number;
  /**
   * Tiered suggestions (rankLocalSpecialists → assembleTiers): first doctors bookable at the profile
   * doctor's own clinic (`clinicCenterIds`), then online-visit doctors based in the city (expertise
   * `onlineIncludeExpertise`, which may admit residents), and the regular in-person list only when
   * both are empty. Without it, the regular in-person list only.
   */
  tiers?: { clinicCenterIds: string[]; onlineIncludeExpertise: string[] };
  /** Scenario mode (plan.ts) — preferred doctors, then online and offices. Takes precedence over `tiers`. */
  plan?: SuggestionPlan;
}

/** One priority group of preferred doctors (all slugs of the same doctor may be listed). */
export interface PreferredGroup {
  slugs: string[];
  /** Profile card heading while one of this group is shown; default localPrivateSpecialistsTitle. */
  title?: string;
  /** Prefix of the line above the button (e.g. «متخصص داخلی») when the group is another specialty. */
  badge?: string;
  /** «مشاهده سایر …» link under the profile card while one of this group is shown (another specialty). */
  more?: { text: string; url: string };
}

/**
 * Owner scenario 2026-10-23 (Dr. Ebrahimi, Dr. Sakhanvar, Dr. Talebian), built from 30 days of Splunk: the
 * doctors patients booked themselves after hitting this profile's full booking come first, then online visit
 * with doctors of the same city, then the in-person list with the platform's best online doctors in between.
 * Only doctors Saman confirms bookable are ever shown. Order and mixing: plan.ts.
 */
export interface SuggestionPlan {
  /** Groups in priority order; inside a group: in-person first, earliest turn, online-visit bonus, satisfaction. */
  preferred: PreferredGroup[];
  /**
   * The profile's own specialty before preferred doctors of another one (owner 2026-10-23, Dr. Sakhanvar:
   * «اصلش گوارشه»): first preferred group, then the city's offices (with national online mixed in), the
   * local online doctors of the specialty (`includeExpertise`) and the remaining national online doctors,
   * then the other groups and the other local online doctors.
   */
  specialtyFirst?: boolean;
  /** More city routes to find preferred / online doctors of another specialty (e.g. `qazvin/internal`). */
  extraRoutes?: { route: string; pages?: number }[];
  /** Online visit with doctors based in `cityName` (search hit with a non-online center in the city). */
  localOnline: {
    includeExpertise: string[];
    excludeExpertise?: string[];
    max: number;
    title: string;
    /** true: mixed into the preferred list (one after every two); false: right after it. */
    mixIntoPreferred: boolean;
  };
  /** The platform's best online doctors of the specialty, any city (search order), mixed between offices. */
  nationalOnline: {
    route: string;
    includeExpertise: string[];
    excludeExpertise?: string[];
    minSatisfaction: number;
    minRates: number;
    max: number;
    title: string;
  };
}

export const LOCAL_PRIVATE_SPECIALISTS: Record<string, LocalPrivateSpecialistsConfig> = {
  'دکتر-عبدالمجید-طاهری-0': {
    city: 'shahrekord',
    cityName: 'شهرکرد',
    groupSlug: 'imaging',
    expertiseName: 'رادیولوژی',
    includeExpertise: ['متخصص رادیولوژی'],
    excludeExpertise: ['دهان و فک'],
  },
  // Dermatologist in Isfahan, often fully booked. Any center type, specialists only (residents have
  // no office licence), well rated, earliest turn first, within ~10 km of his Chaharbagh Bala office
  // (his profile has no coordinates; the point is the mean of the neighbouring Chaharbagh Bala
  // offices). 2026-10-19: 167 dermatology doctors in 4 search pages, ~15 qualified and bookable.
  'دکتر-علی-اصیلیان': {
    city: 'isfahan',
    cityName: 'اصفهان',
    groupSlug: 'dermatology',
    expertiseName: 'پوست',
    includeExpertise: ['متخصص بیماریهای پوست'],
    excludeExpertise: ['دانشجو'],
    privateOfficesOnly: false,
    near: { lat: 32.6275, lon: 51.665, radiusKm: 10 },
    minSatisfaction: 80,
    minRates: 10,
    order: 'first-turn',
    showWhenBookable: true,
    searchPages: 4,
    // Owner request 2026-10-20: his clinic's doctors first (مجتمع حضرت صدیقه طاهره — 10 dermatologists,
    // turns released 07:00), then online visit (incl. dermatology residents such as Hassan Sharifian
    // Dercheh / Elham Abshirini), the in-person list only when neither has anyone.
    tiers: {
      clinicCenterIds: ['b894f993-5968-11eb-9fd5-005056b09c11'],
      onlineIncludeExpertise: ['تخصص بیماریهای پوست'],
    },
  },
  // Orthopedist in Malayer (Fakhrieh clinic + Imam Hossein hospital clinic), often fully booked. Same
  // rules as Dr. Asilian; the point is the Fakhrieh clinic's own coordinates. The malayer route returns
  // the whole Hamadan province (58 doctors on 2026-10-19, only 6 orthopedists in Malayer itself).
  // 2026-10-23, 30 days of Splunk: of 183 visitors who hit his full booking 23 waited for him and ~15
  // booked another Malayer orthopedist (Torkashvand 7, Yousef Sibdari 4, Vafaei 3) — the live card's
  // clicks went to the same doctors — and 3 booked an online orthopedist. No Malayer orthopedist has
  // online visit, so the local online tier stays empty until one does.
  'دکتر-سعید-نوروزی-0': {
    city: 'malayer',
    cityName: 'ملایر',
    groupSlug: 'orthopedics',
    expertiseName: 'ارتوپدی',
    includeExpertise: ['متخصص ارتوپدی'],
    excludeExpertise: ['دانشجو'],
    privateOfficesOnly: false,
    near: { lat: 34.299262, lon: 48.826077, radiusKm: 10 },
    minSatisfaction: 80,
    minRates: 10,
    order: 'first-turn',
    showWhenBookable: true,
    searchPages: 2,
    plan: {
      preferred: [
        {
          slugs: [
            'دکتر-نادر-ترکاشوند-1',
            'دکتر-نادر-ترکاشوند',
            'دکتر-امیر-وفائی',
            'دکتر-سیامک-یوسف-سیبدری',
            'دکتر--سیامک-یوسف-سیبدری',
          ],
        },
      ],
      localOnline: {
        includeExpertise: ['متخصص ارتوپدی'],
        excludeExpertise: ['دانشجو'],
        max: 2,
        title: 'ویزیت آنلاین با پزشکان ارتوپدی ملایر',
        mixIntoPreferred: true,
      },
      nationalOnline: {
        route: 'ir/orthopedics',
        includeExpertise: ['متخصص ارتوپدی'],
        minSatisfaction: 90,
        minRates: 50,
        max: 3,
        title: 'ویزیت آنلاین با پزشکان برتر ارتوپدی',
      },
    },
  },
  // Dermatologist in Kermanshah (Haj Daei clinic), fully booked: of 1016 visitors who hit his full booking in
  // 30 days (to 2026-10-23) ~82 booked another Kermanshah dermatologist within 3 days — mostly these five.
  'دکتر-علی-ابراهیمی-8': {
    city: 'kermanshah',
    cityName: 'کرمانشاه',
    groupSlug: 'dermatology',
    expertiseName: 'پوست',
    includeExpertise: ['متخصص بیماریهای پوست'],
    excludeExpertise: ['دانشجو'],
    privateOfficesOnly: false,
    near: { lat: 34.339626, lon: 47.078578, radiusKm: 10 },
    minSatisfaction: 80,
    minRates: 10,
    order: 'first-turn',
    showWhenBookable: true,
    plan: {
      preferred: [
        {
          slugs: [
            'دکتر-عبدالحمید-ابراهیمی-0',
            'دکتر-آرام-سلیمی',
            'دکتر-آرام-سلیمی-2',
            'دکتر-حسین-کاوسی-3',
            'دکتر-حسین-کاوسی-2',
            'دکتر-هژیر-مهربانی',
            'دکتر-خالد-بابایی',
          ],
        },
      ],
      localOnline: {
        includeExpertise: ['متخصص بیماریهای پوست'],
        excludeExpertise: ['دانشجو'],
        max: 2,
        title: 'ویزیت آنلاین با پزشکان پوست کرمانشاه',
        mixIntoPreferred: true,
      },
      nationalOnline: {
        route: 'ir/dermatology',
        includeExpertise: ['بیماریهای پوست'],
        minSatisfaction: 90,
        minRates: 50,
        max: 3,
        title: 'ویزیت آنلاین با پزشکان برتر پوست',
      },
    },
  },
  // Gastroenterologist in Qazvin, fully booked: Qazvin has 12 gastroenterologists, so patients who hit his
  // full booking went to Dr. Alfati (gastroenterology) or to internists (Dr. Zahedifar, Dr. Khanzadeh).
  // Gastroenterology first (owner: «اصلش گوارشه»): Dr. Alfati, the other Qazvin gastroenterologists,
  // then the internists.
  'دکتر-همایون-سخنور-0': {
    city: 'qazvin',
    cityName: 'قزوین',
    groupSlug: 'gastroenterology',
    expertiseName: 'گوارش',
    includeExpertise: ['گوارش و کبد بالغین'],
    excludeExpertise: ['دانشجو', 'اطفال'],
    privateOfficesOnly: false,
    near: { lat: 36.324622, lon: 49.996918, radiusKm: 10 },
    minSatisfaction: 80,
    minRates: 10,
    order: 'first-turn',
    showWhenBookable: true,
    plan: {
      preferred: [
        { slugs: ['دکتر-حمیده-الفتی-2', 'دکتر-حمیده-الفتی-1'] },
        {
          slugs: [
            'دکتر-آزاده-زاهدی-فر-2',
            'دکتر-آزاده-زاهدی-فر-0',
            'دکتر-فرشته-خان-زاده-جوریانی',
            'دکتر-فرشته-خانزاده-جوریابی-0',
          ],
          title: 'متخصصان داخلی قزوین',
          badge: 'متخصص داخلی',
          more: { text: 'مشاهده سایر متخصصان داخلی قزوین', url: '/s/qazvin/internal/' },
        },
      ],
      specialtyFirst: true,
      extraRoutes: [{ route: 'qazvin/internal', pages: 2 }],
      localOnline: {
        includeExpertise: ['گوارش و کبد بالغین', 'متخصص بیماریهای داخلی'],
        excludeExpertise: ['دانشجو', 'اطفال'],
        max: 3,
        title: 'ویزیت آنلاین با پزشکان گوارش و داخلی قزوین',
        mixIntoPreferred: false,
      },
      nationalOnline: {
        route: 'ir/gastroenterology',
        includeExpertise: ['گوارش و کبد بالغین'],
        excludeExpertise: ['اطفال'],
        minSatisfaction: 90,
        minRates: 20,
        max: 3,
        title: 'ویزیت آنلاین با پزشکان برتر گوارش',
      },
    },
  },
  // Gynecologist in Kashan: 40 of 232 visitors who hit her full booking waited for her; ~40 booked another
  // Kashan gynecologist, mostly these three. The kashan route also returns Isfahan (pages 5+).
  'دکتر-مرضیه-طالبیان-2': {
    city: 'kashan',
    cityName: 'کاشان',
    groupSlug: 'obstetrics-gynecology',
    expertiseName: 'زنان',
    includeExpertise: ['متخصص زنان و زایمان'],
    excludeExpertise: ['دانشجو'],
    privateOfficesOnly: false,
    near: { lat: 34.013416, lon: 51.407019, radiusKm: 10 },
    minSatisfaction: 80,
    minRates: 10,
    order: 'first-turn',
    showWhenBookable: true,
    searchPages: 4,
    plan: {
      preferred: [
        {
          slugs: [
            'دکتر-سارا-فلاح-1',
            'دکتر-سارا-فلاح-2',
            'دکتر-الاله-بلندرفتار',
            'دکتر-طیبه-هاشمی-آرانی',
            'دکتر-طیبه-هاشمی-آرانی-7',
          ],
        },
      ],
      localOnline: {
        includeExpertise: ['متخصص زنان و زایمان'],
        excludeExpertise: ['دانشجو'],
        max: 3,
        title: 'سایر پزشکان آنلاین زنان کاشان',
        mixIntoPreferred: false,
      },
      nationalOnline: {
        route: 'ir/obstetrics-gynecology',
        includeExpertise: ['متخصص زنان و زایمان'],
        excludeExpertise: ['دانشجو'],
        minSatisfaction: 90,
        minRates: 20,
        max: 3,
        title: 'ویزیت آنلاین با پزشکان برتر زنان',
      },
    },
  },
};

/** Own keys only — a slug like `constructor` must not resolve to an Object.prototype member. */
export const getLocalPrivateSpecialistsConfig = (slug?: string | null): LocalPrivateSpecialistsConfig | undefined =>
  slug && Object.prototype.hasOwnProperty.call(LOCAL_PRIVATE_SPECIALISTS, slug) ? LOCAL_PRIVATE_SPECIALISTS[slug] : undefined;

export const localPrivateSpecialistsTitle = (config: LocalPrivateSpecialistsConfig) =>
  `سایر پزشکان ${config.expertiseName} ${config.cityName}`;

/** Search filter value for private-office doctors («پزشکان مطبی»). */
export const PRIVATE_OFFICE_RESULT_TYPE = 'پزشکان مطبی';

/** Search query of a config (also the «همه/سایر پزشکان» link): private offices only unless opted out. */
export const searchFilters = (config: LocalPrivateSpecialistsConfig) =>
  config.privateOfficesOnly === false ? {} : { result_type: PRIVATE_OFFICE_RESULT_TYPE };
